import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LoginServices } from '../../../services/login.service';
import { AlertService } from '../../../services/alert.service';
import BgBody from '../../../components/layout/bg-body.component';
import Input from '../../../components/forms/input.component';
import Button from '../../../components/forms/button.component';
import BgPages from '../../../hooks/use-bg-pages.hook';
import Icon from '../../../components/forms/icon.component';

declare const KTComponents: any;

/**
 * Consume el token que llega en el correo
 * (`${MAIL_PUBLIC_BASE_URL}/reset-password?token=<token>`) contra
 * `POST /v1/auth/reset-password`. El token es de un solo uso y al consumirlo el
 * backend revoca todas las sesiones del usuario, así que al terminar mandamos
 * al login.
 */
export default function ResetPasswordPage() {
  BgPages();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = (params.get('token') ?? '').trim();
  const [errorMessage, setErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues: { newPassword: '', confirmPassword: '' }
  });

  const newPassword = watch('newPassword');

  useEffect(() => {
    KTComponents.init();
  }, []);

  const submit = async (data: any) => {
    setErrorMessage('');
    try {
      await LoginServices.resetPassword({ token, newPassword: data.newPassword });
      AlertService.toastSuccess('Contraseña actualizada. Inicia sesión con la nueva.');
      navigate('/auth/login', { replace: true });
    } catch (error: any) {
      console.error(error);
      setErrorMessage(
        String(error?.error || error?.status || 'El enlace no es válido o ya expiró')
      );
    }
  };

  return (
    <>
      <BgBody />
      <div className="login-hero login-hero--auth text-white bg-p-dark">
        <div className="login-shell--auth d-flex justify-content-center px-2 px-sm-4 position-relative w-100">
          <div className="login-flat login-card--auth device-card device-card--glow shadow-xxl d-flex flex-column justify-content-between">
            <div className="text-center mb-md-10 mt-10">
              <img
                alt="Logo"
                src="/assets/media/logos/voices-gnl-dark.png"
                className="h-45px h-lg-155px logo-default mb-3 mb-md-5 logob z-index-4 img-fluid"
              />
              <h1 className="fs-2x fw-bold mb-1 text-white">Nueva contraseña</h1>
              <div className="text-gray-500 fs-6 fs-md-4">
                Al guardarla se cierran todas tus sesiones abiertas.
              </div>
            </div>

            {errorMessage && (
              <div className="alert alert-danger d-flex align-items-center p-2 bg-transparent animated-Alert mt-4 mb-5">
                <Icon icon="ki-shield-cross" size="fs-2hx" type="danger" className="me-4" />
                <div className="d-flex flex-column flex-grow-1">
                  <h4 className="mb-1 text-white">{t('errors.title-text-alert-atencion')}</h4>
                  <span>{errorMessage}</span>
                </div>
                <button
                  type="button"
                  className="btn btn-icon bg-transparent ms-auto"
                  onClick={() => setErrorMessage('')}
                >
                  <Icon icon="ki-cross" size="fs-1" type="white" />
                </button>
              </div>
            )}

            {token === '' ? (
              <div className="mb-10">
                <div className="alert d-flex align-items-center p-4 bg-transparent border border-warning rounded">
                  <Icon icon="ki-information-5" size="fs-2hx" type="warning" className="me-4" />
                  <div className="d-flex flex-column">
                    <h4 className="mb-1 text-white">Falta el token</h4>
                    <span className="text-gray-500">
                      Abre el enlace completo que te llegó por correo, o pide uno nuevo.
                    </span>
                  </div>
                </div>
                <Link to="/auth/forgot-password" className="btn btn-lg btn-primary w-100 mt-6">
                  Pedir un enlace nuevo
                </Link>
              </div>
            ) : (
              <form className="form mb-10" onSubmit={handleSubmit(submit)}>
                <div className="mb-6 inputb">
                  <Input
                    typeBg="text-white"
                    requiredIndicator="required"
                    uniqueTheme
                    page="login"
                    type="password"
                    label="Contraseña nueva"
                    placeholder="Mínimo 8 caracteres"
                    errors={errors.newPassword}
                    {...register('newPassword', {
                      required: 'Este campo es requerido',
                      minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                      maxLength: { value: 128, message: 'Máximo 128 caracteres' }
                    })}
                  />
                </div>

                <div className="mb-8 inputb">
                  <Input
                    typeBg="text-white"
                    requiredIndicator="required"
                    uniqueTheme
                    page="login"
                    type="password"
                    label="Confirmar contraseña"
                    placeholder="Repite la contraseña"
                    errors={errors.confirmPassword}
                    {...register('confirmPassword', {
                      required: 'Este campo es requerido',
                      validate: (v: string) => v === newPassword || 'Las contraseñas no coinciden'
                    })}
                  />
                </div>

                <Button
                  type="submit"
                  className="btn btn-lg btn-primary w-100 mb-4"
                  disabled={!isValid}
                  isLoading={isSubmitting}
                >
                  Guardar contraseña
                </Button>

                <Link to="/auth/login" className="btn btn-lg btn-link text-gray-500 w-100">
                  Cancelar
                </Link>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
